/* Wrapped in an IIFE so its top-level names stay out of the global scope.
   Theme scripts share one global namespace: when two of them declared the same
   const, the second failed to parse outright and silently did nothing. */
(() => {
  /**
   * <localization-select> — animation and outside-click for the footer's pickers.
   *
   * The markup is a plain <details>, which opens, closes and takes the keyboard on
   * its own; without this script that is exactly what happens, instantly. What a
   * <details> cannot do everywhere is animate: ::details-content is the only handle
   * CSS has on it, and Safari only grew one in 18.4 — the version most phones in the
   * wild are still short of. So the entry and exit are driven from here instead,
   * which works the same in every browser: a class holds the start state, a frame
   * later it is dropped and the transition runs.
   */
  class LocalizationSelect extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.list = this.querySelector('.localization__list');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.list || !this.summary) return;

      this.summary.addEventListener('click', this.#onSummaryClick);
      this.details.addEventListener('toggle', this.#onToggle);
      document.addEventListener('click', this.#onDocumentClick);
      document.addEventListener('keydown', this.#onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.#onDocumentClick);
      document.removeEventListener('keydown', this.#onKeydown);
    }

    #onToggle = () => {
      if (!this.details.open) return;

      // Start where the animation should begin, then let the next frame take the
      // class away so the transition has somewhere to travel from.
      this.list.classList.add('localization__list--entering');
      requestAnimationFrame(() =>
        this.list.classList.remove('localization__list--entering')
      );
    };

    #onSummaryClick = (event) => {
      // Closing is ours to do: letting the browser close it removes the list from the
      // page immediately, and there is nothing left to animate out.
      if (!this.details.open) return;
      event.preventDefault();
      this.close();
    };

    #onDocumentClick = (event) => {
      if (!this.details.open || this.contains(event.target)) return;
      this.close();
    };

    #onKeydown = (event) => {
      if (event.key === 'Escape' && this.details.open) this.close();
    };

    close() {
      if (this.closing) return;
      this.closing = true;
      this.list.classList.add('localization__list--leaving');

      const done = () => {
        this.list.classList.remove('localization__list--leaving');
        this.details.open = false;
        this.closing = false;
      };

      // transitionend is the truth, but a transition that never runs — motion turned
      // off in the theme settings, or reduced at the system level — never reports one.
      const wait = parseFloat(getComputedStyle(this.list).transitionDuration) * 1000;
      if (!wait) return done();

      this.list.addEventListener('transitionend', done, { once: true });
      setTimeout(done, wait + 50);
    }
  }

  if (!customElements.get('localization-select')) {
    customElements.define('localization-select', LocalizationSelect);
  }
})();
